import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  OrderState,
  OrderQuery,
  OrdersResponse,
  OrderResponse,
  TrackingResponse,
  OrderActionsResponse,
  CustomerReturnRequestDto,
  CustomerReplacementRequestDto,
} from "./types";
import { getCookie } from "../../utility/getCookie";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_PAYMENT_APP_BACKEND_PREFIX, // Prefix for PaymentServices
]
  .filter(Boolean)
  .join("");

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
  async (config) => {
    let token = getCookie("access_token");
    if (token === null || token === undefined) {
      try {
        // You might want to trigger a refresh here if needed,
        // but following the requested structure:
        token = getCookie("access_token");
      } catch (err) {
        // Handle failure
      }
    }
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const initialState: OrderState = {
  orders: [],
  currentOrder: null,
  actions: null,
  actionsMap: {},
  tracking: null,
  loading: false,
  csvLoading: false,
  error: null,
  success: false,
  total: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  summary: null,
};

// ============ Async Thunks ============

/**
 * Fetch customer orders
 */
export const fetchMyOrders = createAsyncThunk(
  "customerOrders/fetchMyOrders",
  async (query: OrderQuery, { rejectWithValue }) => {
    try {
      const response = await api.get<OrdersResponse>("/customer/orders", {
        params: query,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch orders",
      );
    }
  },
);

export const fetchMyOrdersSummary = createAsyncThunk(
  "customerOrders/fetchMyOrdersSummary",
  async (query: OrderQuery, { rejectWithValue }) => {
    try {
      const response = await api.get("/customer/orders/summary", {
        params: query,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch orders summary",
      );
    }
  },
);

/**
 * Fetch order by ID
 */
export const fetchOrderById = createAsyncThunk(
  "customerOrders/fetchOrderById",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<OrderResponse>(
        `/customer/orders/${orderId}`,
      );
     
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch order details",
      );
    }
  },
);

/**
 * Fetch Order Actions (cancel/return/replace status)
 */
export const fetchOrderActions = createAsyncThunk(
  "customerOrders/fetchOrderActions",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<OrderActionsResponse>(
        `/customer/orders/${orderId}/actions`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch order actions",
      );
    }
  },
);

/**
 * Fetch Order Tracking (shipment tracking + delivery address + pricing)
 */
export const fetchOrderTracking = createAsyncThunk(
  "customerOrders/fetchOrderTracking",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<TrackingResponse>(
        `/customer/orders/${orderId}/tracking`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch order tracking",
      );
    }
  },
);

/**
 * Fetch order by order number
 */
export const fetchOrderByNumber = createAsyncThunk(
  "customerOrders/fetchOrderByNumber",
  async (orderNumber: string, { rejectWithValue }) => {
    try {
      const response = await api.get<OrderResponse>(
        `/orders/number/${orderNumber}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch order details",
      );
    }
  },
);

/**
 * Fetch order by payment ID
 */
export const fetchOrderByPaymentId = createAsyncThunk(
  "customerOrders/fetchOrderByPaymentId",
  async (paymentId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<OrderResponse>(
        `/orders/payment/${paymentId}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch order by payment ID",
      );
    }
  },
);

/**
 * Cancel order
 */
export const cancelOrder = createAsyncThunk(
  "customerOrders/cancelOrder",
  async (
    { orderId, reason }: { orderId: string; reason: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<OrderResponse>(
        `/customer/orders/${orderId}/cancel`,
        {
          reason,
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to cancel order",
      );
    }
  },
);

/**
 * Request Return
 */
export const requestReturnOrder = createAsyncThunk(
  "customerOrders/requestReturnOrder",
  async (
    {
      orderId,
      reason,
      details,
    }: { orderId: string; reason: string; details?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<OrderResponse>(
        `/customer/orders/${orderId}/return`,
        { reason, details },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to request return",
      );
    }
  },
);

/**
 * Request Replacement
 */
export const requestReplacementOrder = createAsyncThunk(
  "customerOrders/requestReplacementOrder",
  async (
    {
      orderId,
      reason,
      details,
    }: { orderId: string; reason: string; details?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post<OrderResponse>(
        `/customer/orders/${orderId}/replace`,
        { reason, details },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to request replacement",
      );
    }
  },
);



/**
 * Validate products before order
 */
export const validateProducts = createAsyncThunk(
  "customerOrders/validateProducts",
  async (
    items: Array<{ productId: string; quantity: number }>,
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post("/orders/validate-products", { items });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Product validation failed",
      );
    }
  },
);

/**
 * Export orders to CSV
 */
export const exportOrdersToCsv = createAsyncThunk(
  "customerOrders/exportOrdersToCsv",
  async (query: OrderQuery, { rejectWithValue }) => {
    try {
      const response = await api.get("/customer/orders/export/csv", {
        params: query,
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const filename = `my-orders-${new Date().toISOString().split("T")[0]}.csv`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to export CSV",
      );
    }
  },
);

/**
 * Fetch Instore Orders
 */
export const fetchInstoreOrders = createAsyncThunk(
  "customerOrders/fetchInstoreOrders",
  async (query: OrderQuery, { rejectWithValue }) => {
    try {
      const response = await api.get<OrdersResponse>(
        "/customer/orders/type/instore",
        { params: query },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch instore orders",
      );
    }
  },
);

/**
 * Fetch Online Orders
 */
export const fetchOnlineOrders = createAsyncThunk(
  "customerOrders/fetchOnlineOrders",
  async (query: OrderQuery, { rejectWithValue }) => {
    try {
      const response = await api.get<OrdersResponse>(
        "/customer/orders/type/online",
        { params: query },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch online orders",
      );
    }
  },
);

/**
 * Fetch Pending Orders
 */
export const fetchPendingOrders = createAsyncThunk(
  "customerOrders/fetchPendingOrders",
  async (query: OrderQuery, { rejectWithValue }) => {
    try {
      const response = await api.get<OrdersResponse>(
        "/customer/orders/status/pending",
        { params: query },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pending orders",
      );
    }
  },
);

/**
 * Fetch Completed Orders
 */
export const fetchCompletedOrders = createAsyncThunk(
  "customerOrders/fetchCompletedOrders",
  async (query: OrderQuery, { rejectWithValue }) => {
    try {
      const response = await api.get<OrdersResponse>(
        "/customer/orders/status/completed",
        { params: query },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch completed orders",
      );
    }
  },
);

const customerOrderSlice = createSlice({
  name: "customerOrders",
  initialState,
  reducers: {
    clearOrderState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
      state.tracking = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch My Orders
      .addCase(fetchMyOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchMyOrders.fulfilled,
        (state, action: PayloadAction<OrdersResponse>) => {
          state.loading = false;
          state.orders = action.payload.data.orders;
          state.total = action.payload.data.pagination.total;
          state.page = action.payload.data.pagination.page;
          state.limit = action.payload.data.pagination.limit;
          state.totalPages = action.payload.data.pagination.totalPages;
        },
      )
      .addCase(fetchMyOrders.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Summary
      .addCase(fetchMyOrdersSummary.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchMyOrdersSummary.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.summary = action.payload.data;
        },
      )
      .addCase(
        fetchMyOrdersSummary.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        },
      )

      // Fetch Order By ID
      .addCase(
        fetchOrderById.fulfilled,
        (state, action: PayloadAction<OrderResponse>) => {
          state.loading = false;
          state.currentOrder = action.payload.data;
        },
      )

      // Fetch Order By Number
      .addCase(
        fetchOrderByNumber.fulfilled,
        (state, action: PayloadAction<OrderResponse>) => {
          state.loading = false;
          state.currentOrder = action.payload.data;
        },
      )

      // Fetch Order By Payment ID
      .addCase(
        fetchOrderByPaymentId.fulfilled,
        (state, action: PayloadAction<OrderResponse>) => {
          state.loading = false;
          state.currentOrder = action.payload.data;
        },
      )

      // Fetch Order Tracking
      .addCase(fetchOrderTracking.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchOrderTracking.fulfilled,
        (state, action: PayloadAction<TrackingResponse>) => {
          state.loading = false;
          state.tracking = action.payload.data;
        },
      )
      .addCase(fetchOrderTracking.rejected, (state) => {
        state.loading = false;
      })

      // Cancel Order
      .addCase(cancelOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(
        cancelOrder.fulfilled,
        (state, action: PayloadAction<OrderResponse>) => {
          state.loading = false;
          state.success = true;
          state.currentOrder = action.payload.data;
          // Update order in list if present
          const index = state.orders.findIndex(
            (o) => o.id === action.payload.data.id,
          );
          if (index !== -1) {
            state.orders[index] = action.payload.data;
          }
        },
      )
      .addCase(cancelOrder.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // Fetch Order Actions
      .addCase(
        fetchOrderActions.fulfilled,
        (state, action: any) => {
          state.loading = false;
          state.actions = action.payload.data;
          state.actionsMap[action.meta.arg] = action.payload.data;
        },
      )

      // Request Return
      .addCase(requestReturnOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(
        requestReturnOrder.fulfilled,
        (state, action: PayloadAction<OrderResponse>) => {
          state.loading = false;
          state.success = true;
          state.currentOrder = action.payload.data;
          const index = state.orders.findIndex(
            (o) => o.id === action.payload.data.id,
          );
          if (index !== -1) {
            state.orders[index] = action.payload.data;
          }
        },
      )

      // Request Replacement
      .addCase(requestReplacementOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(
        requestReplacementOrder.fulfilled,
        (state, action: PayloadAction<OrderResponse>) => {
          state.loading = false;
          state.success = true;
          state.currentOrder = action.payload.data;
          const index = state.orders.findIndex(
            (o) => o.id === action.payload.data.id,
          );
          if (index !== -1) {
            state.orders[index] = action.payload.data;
          }
        },
      )

      // Export CSV Loading
      .addCase(exportOrdersToCsv.pending, (state) => {
        state.csvLoading = true;
      })
      .addCase(exportOrdersToCsv.fulfilled, (state) => {
        state.csvLoading = false;
      })
      .addCase(exportOrdersToCsv.rejected, (state) => {
        state.csvLoading = false;
      })

  

      // Combined Handle for all filtered order list fetchers
      .addMatcher(
        (action) =>
          [
            fetchInstoreOrders.fulfilled.type,
            fetchOnlineOrders.fulfilled.type,
            fetchPendingOrders.fulfilled.type,
            fetchCompletedOrders.fulfilled.type,
          ].includes(action.type),
        (state, action: PayloadAction<OrdersResponse>) => {
          state.loading = false;
          state.orders = action.payload.data.orders;
          state.total = action.payload.data.pagination.total;
          state.page = action.payload.data.pagination.page;
          state.limit = action.payload.data.pagination.limit;
          state.totalPages = action.payload.data.pagination.totalPages;
        },
      )

      // Global Matchers for common cases
      // fetchOrderActions is excluded because it is a background fetch — letting it
      // set loading=true would hide the orders list and unmount/remount the action
      // buttons, which re-triggers the fetch and creates an infinite loop.
      .addMatcher(
        (action) =>
          action.type.endsWith("/pending") &&
          action.type.startsWith("customerOrders/") &&
          action.type !== fetchOrderActions.pending.type,
        (state) => {
          state.loading = true;
          state.error = null;
        },
      )
      .addMatcher(
        (action) =>
          action.type.endsWith("/rejected") &&
          action.type.startsWith("customerOrders/") &&
          action.type !== fetchOrderActions.rejected.type,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        },
      );
  },
});

export const { clearOrderState, clearCurrentOrder } =
  customerOrderSlice.actions;
export default customerOrderSlice.reducer;
